import React, { createContext, useContext, useMemo } from "react";

import { QueryClient } from "@tanstack/react-query";

import { RekazSDK } from "@rekaz/core";

interface SDKContextValue {
  sdk: RekazSDK;
  isInitialized: boolean;
  queryClient: QueryClient;
}

const SDKContext = createContext<SDKContextValue | null>(null);

interface SDKProviderProps {
  children: React.ReactNode;
  sdkInstance: RekazSDK;
}

export function SDKProvider({ children, sdkInstance }: SDKProviderProps) {
  const queryClient = useMemo(
    () => sdkInstance.getQueryClient(),
    [sdkInstance]
  );

  const isInitialized = !!(sdkInstance && queryClient);

  const value: SDKContextValue = useMemo(
    () => ({
      sdk: sdkInstance,
      isInitialized,
      queryClient
    }),
    [sdkInstance, isInitialized, queryClient]
  );

  return <SDKContext.Provider value={value}>{children}</SDKContext.Provider>;
}

export function useSDK(): SDKContextValue {
  const context = useContext(SDKContext);
  if (!context) {
    throw new Error("useSDK must be used within a SDKProvider");
  }
  return context;
}

export function invalidateSDKCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ["tenantConfig"] });
  queryClient.invalidateQueries({ queryKey: ["cart"] });
}
